import type { ConsentManagerConfig } from '@/core/types';

export class BannerStyles {
  public static getStyles(config: ConsentManagerConfig): string {
    const theme = config.theme || 'light';
    const position = config.position || 'bottom';

    return `
      :host {
        all: initial;
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
        font-size: 14px;
        line-height: 1.5;
        color: ${this.getColor(theme, 'text')};
        z-index: 2147483647;
        position: fixed;
        ${position}: 0;
        left: 0;
        right: 0;
        transform: translateY(${position === 'bottom' ? '100%' : '-100%'});
        transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        box-sizing: border-box;
      }

      :host(.cmp-banner--visible) {
        transform: translateY(0);
      }

      .cmp-banner__container {
        background: ${this.getColor(theme, 'background')};
        border-top: ${position === 'bottom' ? `1px solid ${this.getColor(theme, 'border')}` : 'none'};
        border-bottom: ${position === 'top' ? `1px solid ${this.getColor(theme, 'border')}` : 'none'};
        box-shadow: ${position === 'bottom' ? '0 -2px 10px rgba(0, 0, 0, 0.1)' : '0 2px 10px rgba(0, 0, 0, 0.1)'};
        padding: 16px 20px;
        position: relative;
      }

      .cmp-banner__content {
        max-width: 1200px;
        margin: 0 auto;
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 24px;
        flex-wrap: wrap;
      }

      @media (max-width: 768px) {
        .cmp-banner__content {
          flex-direction: column;
          align-items: stretch;
          gap: 16px;
        }
      }

      .cmp-banner__text {
        flex: 1;
        min-width: 0;
      }

      .cmp-banner__title {
        margin: 0 0 8px 0;
        font-size: 16px;
        font-weight: 600;
        color: ${this.getColor(theme, 'text')};
      }

      .cmp-banner__description {
        margin: 0;
        color: ${this.getColor(theme, 'textSecondary')};
        line-height: 1.4;
      }

      .cmp-banner__actions {
        display: flex;
        gap: 12px;
        flex-shrink: 0;
        align-items: center;
      }

      @media (max-width: 768px) {
        .cmp-banner__actions {
          flex-direction: column;
          align-items: stretch;
        }
      }

      .cmp-banner__button {
        border: none;
        border-radius: 6px;
        padding: 10px 16px;
        font-size: 14px;
        font-weight: 500;
        cursor: pointer;
        transition: all 0.2s ease;
        white-space: nowrap;
        min-width: auto;
        font-family: inherit;
      }

      .cmp-banner__button:focus {
        outline: 2px solid ${this.getColor(theme, 'primary')};
        outline-offset: 2px;
      }

      .cmp-banner__button--accept {
        background: ${this.getColor(theme, 'primary')};
        color: white;
      }

      .cmp-banner__button--accept:hover {
        background: ${this.getColor(theme, 'primaryHover')};
        transform: translateY(-1px);
      }

      .cmp-banner__button--reject,
      .cmp-banner__button--customize {
        background: ${this.getColor(theme, 'buttonSecondary')};
        color: ${this.getColor(theme, 'text')};
        border: 1px solid ${this.getColor(theme, 'border')};
      }

      .cmp-banner__button--reject:hover,
      .cmp-banner__button--customize:hover {
        background: ${this.getColor(theme, 'buttonSecondaryHover')};
        transform: translateY(-1px);
      }

      @media (max-width: 480px) {
        .cmp-banner__container {
          padding: 12px 16px;
        }
        
        .cmp-banner__title {
          font-size: 15px;
        }
        
        .cmp-banner__button {
          padding: 12px 16px;
          font-size: 15px;
        }
      }

      /* Accessibility improvements */
      @media (prefers-reduced-motion: reduce) {
        :host,
        .cmp-banner__button,
        .cmp-banner__close {
          transition: none;
        }
      }

      @media (prefers-contrast: high) {
        .cmp-banner__container {
          border: 2px solid ${this.getColor(theme, 'text')};
        }
        
        .cmp-banner__button {
          border: 2px solid currentColor;
        }
      }

      ${config.customCSS || ''}
    `;
  }

  private static getColor(theme: string, colorName: string): string {
    const themes = {
      light: {
        background: '#ffffff',
        text: '#1a1a1a',
        textSecondary: '#666666',
        primary: '#2563eb',
        primaryHover: '#1d4ed8',
        buttonSecondary: '#f8fafc',
        buttonSecondaryHover: '#f1f5f9',
        border: '#e2e8f0',
      },
      dark: {
        background: '#1f2937',
        text: '#f9fafb',
        textSecondary: '#9ca3af',
        primary: '#3b82f6',
        primaryHover: '#2563eb',
        buttonSecondary: '#374151',
        buttonSecondaryHover: '#4b5563',
        border: '#4b5563',
      },
      auto: {
        background: 'light-dark(#ffffff, #1f2937)',
        text: 'light-dark(#1a1a1a, #f9fafb)',
        textSecondary: 'light-dark(#666666, #9ca3af)',
        primary: 'light-dark(#2563eb, #3b82f6)',
        primaryHover: 'light-dark(#1d4ed8, #2563eb)',
        buttonSecondary: 'light-dark(#f8fafc, #374151)',
        buttonSecondaryHover: 'light-dark(#f1f5f9, #4b5563)',
        border: 'light-dark(#e2e8f0, #4b5563)',
      },
    };

    return (
      themes[theme as keyof typeof themes]?.[
        colorName as keyof typeof themes.light
      ] || themes.light[colorName as keyof typeof themes.light]
    );
  }

  public static getPositionStyles(position: 'top' | 'bottom'): string {
    return position === 'top'
      ? 'top: 0; bottom: auto; transform: translateY(-100%);'
      : 'bottom: 0; top: auto; transform: translateY(100%);';
  }
}
